/**
 * LINE-00 ↔ LIFF deep-link contract helpers.
 * Agent Flex cards and LIFF must open the same opaque object + revision.
 */

import {
  FINANCIAL_WORK_ACTION_PARAM,
  FINANCIAL_WORK_NAV_PARAM,
  FINANCIAL_WORK_OBJECT_PARAM,
  FINANCIAL_WORK_REV_ID_PARAM,
  FINANCIAL_WORK_REV_PARAM,
} from "./nav";
import {
  applyNamedObjectQuery,
  encodeObjectRef,
  parseObjectRef,
  parseObjectRefFromSearch,
} from "./intent";
import type {
  LiffFinancialWorkCard,
  LineFinancialWorkCard,
  ObjectRef,
} from "./types";

/** Query keys that must never appear on a LINE→LIFF financial-work deep link. */
const FORBIDDEN_DEEP_LINK_KEYS = [
  "token",
  "access_token",
  "auth_token",
  "id_token",
  "refresh_token",
  "accountNumber",
  "account_number",
  "bankAccount",
  "nationalId",
  "slipUrl",
  "evidenceUrl",
  "imageUrl",
  "phone",
  "email",
] as const;

/** Retired client-fixture flags — old bookmarks must not re-enter demo mode. */
export const FINANCIAL_WORK_DEMO_QUERY_KEYS = [
  "fwDemo",
  "fwDemoScene",
] as const;

const STRIP_QUERY_KEYS = [
  ...FORBIDDEN_DEEP_LINK_KEYS,
  ...FINANCIAL_WORK_DEMO_QUERY_KEYS,
] as const;

/** Drop privacy keys and retired demo flags from a search string. */
export function stripFinancialWorkQueryParams(search: string): {
  next: string;
  stripped: string[];
} {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const stripped: string[] = [];
  for (const key of STRIP_QUERY_KEYS) {
    if (params.has(key)) {
      stripped.push(key);
      params.delete(key);
    }
  }
  return { next: params.toString(), stripped };
}

/** Strip retired/privacy keys and rewrite packed `object=kind:id&rev=9` to name pointers. */
export function normalizeFinancialWorkSearch(search: string): {
  next: string;
  stripped: string[];
  migrated: boolean;
} {
  const { next: strippedNext, stripped } = stripFinancialWorkQueryParams(search);
  const params = new URLSearchParams(strippedNext);
  const ref = parseObjectRefFromSearch(params);
  let migrated = false;
  if (ref && !isNamedPointerSearch(params)) {
    const revision = parseRevisionFromSearch(params);
    applyNamedObjectQuery(params, ref, revision);
    migrated = true;
  }
  return { next: params.toString(), stripped, migrated };
}

function isNamedPointerSearch(params: URLSearchParams): boolean {
  const objectVal = params.get(FINANCIAL_WORK_OBJECT_PARAM)?.trim() || "";
  if (!objectVal || objectVal.includes(":")) return false;
  if (!(params.get(objectVal)?.trim())) return false;
  const revVal = params.get(FINANCIAL_WORK_REV_PARAM)?.trim() || "";
  if (!revVal) return true;
  if (revVal === FINANCIAL_WORK_REV_ID_PARAM) return true;
  return parseProjectionRevision(revVal) == null;
}

/** `rev=rev_id` + `rev_id=9`; packed numeric `rev=9` still parses. */
export function parseRevisionFromSearch(
  params: { get(name: string): string | null },
): number | null {
  const revVal = params.get(FINANCIAL_WORK_REV_PARAM)?.trim() || "";
  if (revVal && parseProjectionRevision(revVal) == null) {
    const pointed = parseProjectionRevision(params.get(revVal));
    if (pointed != null) return pointed;
  }
  return (
    parseProjectionRevision(revVal) ??
    parseProjectionRevision(params.get(FINANCIAL_WORK_REV_ID_PARAM))
  );
}

export type FinancialWorkLineDeepLink = {
  objectRef: ObjectRef;
  projectionRevision?: number;
  actionId?: string;
  nav?: "work";
  workspaceId?: string;
  serviceId?: string;
  source?: string;
};

export type ParsedFinancialWorkLineDeepLink = FinancialWorkLineDeepLink & {
  /** True when the URL carried forbidden privacy-sensitive keys (stripped). */
  strippedSensitiveKeys: string[];
};

export function parseProjectionRevision(
  raw: string | null | undefined,
): number | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return null;
  return n;
}

/**
 * Build a privacy-safe LIFF detail path for a LINE compact card.
 * `object=receivable_batch_id` names the id param; `rev=rev_id` names the revision param.
 */
export function buildFinancialWorkLineDetailHref(
  args: FinancialWorkLineDeepLink & { pathname?: string },
): string {
  const params = new URLSearchParams();
  params.set(FINANCIAL_WORK_NAV_PARAM, "work");
  applyNamedObjectQuery(
    params,
    args.objectRef,
    args.projectionRevision ?? null,
  );
  if (args.actionId?.trim()) {
    params.set(FINANCIAL_WORK_ACTION_PARAM, args.actionId.trim());
  }
  if (args.workspaceId?.trim())
    params.set("workspaceId", args.workspaceId.trim());
  if (args.serviceId?.trim()) params.set("serviceId", args.serviceId.trim());
  if (args.source?.trim()) params.set("source", args.source.trim());
  else params.set("source", "order");

  const q = params.toString();
  const path = args.pathname?.trim() || "";
  return path ? `${path}?${q}` : `?${q}`;
}

export function parseFinancialWorkLineDeepLink(
  search: string,
): ParsedFinancialWorkLineDeepLink | null {
  const { next, stripped: strippedSensitiveKeys } =
    stripFinancialWorkQueryParams(search);
  const params = new URLSearchParams(next);

  const objectRef = parseObjectRefFromSearch(params);
  if (!objectRef) return null;

  return {
    objectRef,
    projectionRevision: parseRevisionFromSearch(params) ?? undefined,
    actionId: params.get(FINANCIAL_WORK_ACTION_PARAM)?.trim() || undefined,
    nav: "work",
    workspaceId: params.get("workspaceId")?.trim() || undefined,
    serviceId: params.get("serviceId")?.trim() || undefined,
    source: params.get("source")?.trim() || undefined,
    strippedSensitiveKeys,
  };
}

/** Compare LINE card revision in the URL with the live LIFF projection card. */
export function resolveLineEntryStaleState(args: {
  entryRevision: number | null;
  cardRevision: number | null | undefined;
}): {
  kind: "unknown" | "current" | "stale" | "ahead";
  entryRevision: number | null;
  cardRevision: number | null;
} {
  const entry = args.entryRevision;
  const card =
    args.cardRevision == null || !Number.isFinite(args.cardRevision)
      ? null
      : args.cardRevision;
  if (entry == null || card == null) {
    return { kind: "unknown", entryRevision: entry, cardRevision: card };
  }
  if (entry === card) {
    return { kind: "current", entryRevision: entry, cardRevision: card };
  }
  if (entry < card) {
    return { kind: "stale", entryRevision: entry, cardRevision: card };
  }
  return { kind: "ahead", entryRevision: entry, cardRevision: card };
}

/** Map a LIFF work card into the LINE compact card shape (for fixtures / continuity). */
export function toLineFinancialWorkCard(
  card: LiffFinancialWorkCard,
  options?: { detailLink?: string; issuedAt?: string },
): LineFinancialWorkCard {
  return {
    objectRef: card.objectRef,
    caseRef: card.caseRef,
    projectionRevision: card.projectionRevision,
    audience: card.audience,
    title: card.title,
    receiptLines: [
      card.subtitle,
      card.nextStep.label,
      card.requirementStatus.summary,
    ].filter((line): line is string => Boolean(line && line.trim())),
    amountSummary: card.amountSummary
      ? {
          currency: card.amountSummary.currency,
          primaryAmountMinor: card.amountSummary.primaryAmountMinor,
          basis: card.amountSummary.basis,
        }
      : undefined,
    workPlacement: card.workPlacement,
    requirementStatus: card.requirementStatus,
    nextStep: card.nextStep,
    primaryAction: card.availableActions[0],
    secondaryAction: card.availableActions[1],
    detailLink: options?.detailLink ?? card.detailLink,
    issuedAt: options?.issuedAt ?? new Date().toISOString(),
    freshness: card.freshness === "current" ? "current" : card.freshness,
  };
}

/** Opaque postback data builder — no amounts or private identifiers. */
export function buildLineFinancialWorkPostbackData(args: {
  actionId: string;
  objectRef: ObjectRef;
  projectionRevision: number;
  operationKey?: string;
}): string {
  const params = new URLSearchParams();
  params.set("actionId", args.actionId.trim());
  params.set("object", encodeObjectRef(args.objectRef));
  params.set("rev", String(args.projectionRevision));
  if (args.operationKey?.trim()) {
    params.set("operationKey", args.operationKey.trim());
  }
  return params.toString();
}

export function parseLineFinancialWorkPostbackData(
  raw: string | null | undefined,
): {
  actionId: string;
  objectRef: ObjectRef;
  projectionRevision: number;
  operationKey?: string;
} | null {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  const params = new URLSearchParams(s);
  const actionId = params.get("actionId")?.trim() || "";
  const objectRef = parseObjectRef(params.get("object"));
  const projectionRevision = parseProjectionRevision(params.get("rev"));
  if (!actionId || !objectRef || projectionRevision == null) return null;
  const operationKey = params.get("operationKey")?.trim() || undefined;
  return { actionId, objectRef, projectionRevision, operationKey };
}
